import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

export type TableVariant = 'default' | 'ghost';

const TableVariantContext = React.createContext<TableVariant>('default');

const tableHeadVariants = cva(
  'text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
  {
    variants: {
      variant: {
        default: 'h-10 px-2',
        ghost: 'h-14 px-4 text-sm text-primary',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const tableCellVariants = cva(
  'text-left align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
  {
    variants: {
      variant: {
        default: 'px-2',
        ghost: 'px-4 py-4',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  variant?: TableVariant;
  fixed?: boolean;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, variant = 'default', fixed = false, ...props }, ref) => (
    <TableVariantContext.Provider value={variant}>
      <div className="relative w-full overflow-auto">
        <table
          ref={ref}
          className={cn('w-full caption-bottom text-sm', fixed && 'table-fixed border-collapse', className)}
          {...props}
        />
      </div>
    </TableVariantContext.Provider>
  )
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
);
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => {
    const variant = React.useContext(TableVariantContext);

    return (
      <tbody
        ref={ref}
        className={cn(variant === 'ghost' ? undefined : '[&_tr:last-child]:border-0', className)}
        {...props}
      />
    );
  }
);
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => {
    const variant = React.useContext(TableVariantContext);

    return (
      <tfoot
        ref={ref}
        className={cn(
          variant === 'ghost'
            ? 'border-t font-medium [&>tr]:last:border-b-0'
            : 'bg-muted/50 border-t font-medium [&>tr]:last:border-b-0',
          className
        )}
        {...props}
      />
    );
  }
);
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => {
    const variant = React.useContext(TableVariantContext);

    return (
      <tr
        ref={ref}
        className={cn(
          'group border-b transition-colors',
          variant === 'ghost'
            ? 'hover:bg-transparent data-[state=selected]:bg-transparent'
            : 'hover:bg-muted/50 data-[state=selected]:bg-muted',
          className
        )}
        {...props}
      />
    );
  }
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => {
    const variant = React.useContext(TableVariantContext);

    return (
      <th
        ref={ref}
        className={cn(tableHeadVariants({ variant }), className)}
        style={{ width: props.style?.width }}
        {...props}
      />
    );
  }
);
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => {
    const variant = React.useContext(TableVariantContext);

    return (
      <td
        ref={ref}
        className={cn(tableCellVariants({ variant }), className)}
        style={{ width: props.style?.width }}
        {...props}
      />
    );
  }
);
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn('text-muted-foreground mt-4 text-sm', className)} {...props} />
  )
);
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
